import { BusinessCardData } from '@/types';

/**
 * Generates a vCard (VCF) file content from business card data
 * Follows vCard 3.0 specification
 */
export const generateVCard = (data: BusinessCardData): string => {
  const lines: string[] = [];
  
  // vCard header
  lines.push('BEGIN:VCARD');
  lines.push('VERSION:3.0');
  
  // Full name
  if (data.displayName) {
    lines.push(`FN:${escapeVCard(data.displayName)}`);
  }
  
  // Structured name (Family name;Given name;Additional names;Prefix;Suffix)
  const surname = data.surname || '';
  const givenName = data.givenName || '';
  lines.push(`N:${escapeVCard(surname)};${escapeVCard(givenName)};;;`);
  
  // Organization
  if (data.companyName || data.department) {
    const org = [data.companyName, data.department].filter(Boolean).join(';');
    lines.push(`ORG:${escapeVCard(org)}`);
  }
  
  // Job title
  if (data.jobTitle) {
    lines.push(`TITLE:${escapeVCard(data.jobTitle)}`);
  }
  
  // Primary email
  if (data.mail) {
    lines.push(`EMAIL;TYPE=INTERNET,WORK,PREF:${escapeVCard(data.mail)}`);
  }
  
  // Additional emails
  data.additionalEmails?.forEach((email) => {
    if (email) {
      lines.push(`EMAIL;TYPE=INTERNET,WORK:${escapeVCard(email)}`);
    }
  });
  
  // Mobile phone
  if (data.mobilePhone) {
    lines.push(`TEL;TYPE=CELL,VOICE,PREF:${escapeVCard(data.mobilePhone)}`);
  }
  
  // Business phones
  data.businessPhones?.forEach((phone) => {
    if (phone) {
      lines.push(`TEL;TYPE=WORK,VOICE:${escapeVCard(phone)}`);
    }
  });
  
  // Additional phones
  data.additionalPhones?.forEach((phone) => {
    if (phone.number) {
      const type = phone.type.toUpperCase();
      lines.push(`TEL;TYPE=${type},VOICE:${escapeVCard(phone.number)}`);
    }
  });
  
  // Primary address
  if (data.address) {
    const { street, city, state, postalCode, country } = data.address;
    const addressType = data.address.type === 'HOME' ? 'HOME' : 'WORK';
    // ADR format: PO Box;Extended Address;Street;City;State;Postal Code;Country
    lines.push(
      `ADR;TYPE=${addressType}:;;${escapeVCard(street || '')};${escapeVCard(city || '')};${escapeVCard(state || '')};${escapeVCard(postalCode || '')};${escapeVCard(country || '')}`
    );
  }
  
  // Additional addresses
  data.additionalAddresses?.forEach((address) => {
    const { street, city, state, postalCode, country, type } = address;
    const addressType = type === 'HOME' ? 'HOME' : 'WORK';
    lines.push(
      `ADR;TYPE=${addressType}:;;${escapeVCard(street || '')};${escapeVCard(city || '')};${escapeVCard(state || '')};${escapeVCard(postalCode || '')};${escapeVCard(country || '')}`
    );
  });
  
  // Website
  if (data.website) {
    lines.push(`URL;TYPE=WORK:${escapeVCard(data.website)}`);
  }
  
  // Social profiles
  data.socialProfiles?.forEach((profile) => {
    if (profile.url) {
      lines.push(`URL;TYPE=${escapeVCard(profile.platform)}:${escapeVCard(profile.url)}`);
    }
  });
  
  // Notes
  if (data.notes) {
    lines.push(`NOTE:${escapeVCard(data.notes)}`);
  }
  
  // Business sector as category
  if (data.businessSector) {
    lines.push(`CATEGORIES:${escapeVCard(data.businessSector)}`);
  }
  
  // Revision timestamp
  const now = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  lines.push(`REV:${now}`);
  
  // vCard footer
  lines.push('END:VCARD');
  
  return lines.join('\r\n');
};

/**
 * Escape special characters in vCard fields
 */
const escapeVCard = (text: string): string => {
  if (!text) return '';
  return text
    .replace(/\\/g, '\\\\')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;')
    .replace(/\n/g, '\\n');
};

/**
 * Download vCard file
 */
export const downloadVCard = (data: BusinessCardData): void => {
  const vCardContent = generateVCard(data);
  const blob = new Blob([vCardContent], { type: 'text/vcard;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  
  // Handle undefined or empty displayName
  const name = data.displayName || data.givenName || data.surname || 'contact';
  const fileName = name.replace(/\s+/g, '-').toLowerCase();
  link.download = `${fileName}.vcf`;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
};
